import { useState } from 'react';
import { toast } from 'sonner';
import { useGmbLivePreview } from '../../hooks/useGmbGovernance';
import GMBEmptyState from './shared/GMBEmptyState';
import { GBMContentWrapper } from './shared/GMBLoadingState';

interface LivePreviewProps {
  onNavigate?: (section: string) => void;
}

const checkStatusStyle: Record<string, { bg: string; icon: string; dot: string; text: string }> = {
  good: {
    bg: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
    icon: 'ri-check-line text-green-600',
    dot: 'bg-green-500',
    text: 'text-green-700 dark:text-green-300',
  },
  warning: {
    bg: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
    icon: 'ri-alert-line text-amber-600',
    dot: 'bg-amber-500',
    text: 'text-amber-700 dark:text-amber-300',
  },
  critical: {
    bg: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
    icon: 'ri-error-warning-line text-red-600',
    dot: 'bg-red-500',
    text: 'text-red-700 dark:text-red-300',
  },
};

const monitoringIcons: Record<string, string> = {
  'Business Hours': 'ri-time-line',
  'Missing Profile Info': 'ri-file-warning-line',
  'Profile Completeness': 'ri-percent-line',
  'Category': 'ri-account-box-line',
  'NAP Consistency': 'ri-map-pin-user-line',
  'Photo Availability': 'ri-image-line',
  'Posting Activity': 'ri-calendar-check-line',
};

const monitoringNavMap: Record<string, string> = {
  'Business Hours': 'as-is-state',
  'Missing Profile Info': 'as-is-state',
  'Profile Completeness': 'as-is-state',
  'Category': 'as-is-state',
  'NAP Consistency': 'as-is-state',
  'Photo Availability': 'photo-upload',
  'Posting Activity': 'post-scheduler',
};

function MediaThumb({ src, children }: { src?: string; children?: React.ReactNode }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className="w-24 h-16 rounded-md overflow-hidden flex-shrink-0 flex items-center justify-center bg-gray-100 dark:bg-gray-700">
      {src && !failed ? (
        <img
          src={src}
          alt=""
          referrerPolicy="no-referrer"
          className="w-full h-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        children
      )}
    </div>
  );
}

export default function LivePreview({ onNavigate }: LivePreviewProps) {
  const [viewMode, setViewMode] = useState<'mobile' | 'desktop'>('mobile');
  const { data: lpData, isLoading, error, refetch } = useGmbLivePreview();

  return (
    <GBMContentWrapper error={error} onRetry={() => refetch()} loading={isLoading}>
      <Inner viewMode={viewMode} setViewMode={setViewMode} onNavigate={onNavigate} data={lpData?.data || {}} />
    </GBMContentWrapper>
  );
}

function Inner({
  viewMode,
  setViewMode,
  onNavigate,
  data,
}: {
  viewMode: 'mobile' | 'desktop';
  setViewMode: (m: 'mobile' | 'desktop') => void;
  onNavigate?: (section: string) => void;
  data: any;
}) {
  const listing = data.listing || {};
  const checklist = data.listing_health_checklist || [];
  const monitoring = data.profile_monitoring || [];

  const flaggedIssues = monitoring.filter((c: any) => c.severity === 'warning' || c.severity === 'critical');

  const listingMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([listing.business_name, listing.address].filter(Boolean).join(' '))}`;

  const handleShare = async () => {
    const shareData = {
      title: listing.business_name || 'Your Business',
      text: `${listing.business_name || 'Your Business'} on Google`,
      url: listingMapsUrl,
    };
    try {
      if (typeof navigator.share === 'function') {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(listingMapsUrl);
        toast.success('Link copied to clipboard');
      }
    } catch (err) {
      // user cancelled share — no action
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex bg-gray-100 dark:bg-gray-800 rounded-full p-1">
          {(['mobile', 'desktop'] as const).map((mode) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                viewMode === mode ? 'bg-teal-600 text-white' : 'text-gray-600 dark:text-gray-300 hover:text-gray-800'
              }`}
            >
              {mode === 'mobile' ? 'Mobile' : 'Desktop'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg px-3 py-2">
          <i className="ri-information-line text-yellow-600 text-sm"></i>
          <span className="text-xs text-yellow-700 dark:text-yellow-300">Changes reflect once published.</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className={`${viewMode === 'mobile' ? 'lg:col-span-1' : 'lg:col-span-2'}`}>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
            <div className="flex gap-1 p-2 bg-gray-50 dark:bg-gray-900 overflow-x-auto">
              {(listing.photos || []).slice(0, 4).map((url: string, i: number) => (
                <MediaThumb key={i} src={url}>
                  <i className="ri-image-line text-gray-400 text-xl" />
                </MediaThumb>
              ))}
              {(listing.videos || []).slice(0, 4).map((v: any, i: number) => (
                <MediaThumb key={`v${i}`} src={v?.thumbnail_url}>
                  <i className="ri-play-circle-line text-gray-400 text-xl" />
                </MediaThumb>
              ))}
              {(listing.photos || []).length === 0 && (listing.videos || []).length === 0 && (
                <div className="w-full h-16 bg-gray-100 dark:bg-gray-700 rounded-md flex items-center justify-center text-gray-400 text-xs">
                  No photos or videos yet
                </div>
              )}
            </div>

            <div className="p-4 space-y-3">
              <div className="flex items-center gap-3">
                {listing.profile_photo_url ? (
                  <img
                    src={listing.profile_photo_url}
                    alt={`${listing.business_name || 'Business'} profile icon`}
                    referrerPolicy="no-referrer"
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0 border border-gray-200 dark:border-gray-700"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0 text-sm font-bold text-gray-500 dark:text-gray-400">
                    {(listing.business_name || 'B').charAt(0).toUpperCase()}
                  </div>
                )}
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">{listing.business_name || 'Your Business'}</h2>
              </div>

              <div className="flex items-center gap-3 text-sm">
                <div className="flex items-center gap-1">
                  <span className="font-bold text-gray-900 dark:text-white">{listing.rating || '0.0'}</span>
                  <i className="ri-star-fill text-yellow-400 text-sm"></i>
                </div>
                <span className="text-gray-500">({listing.review_count || 0} reviews)</span>
                <span className="text-gray-400">·</span>
                <span className="text-gray-600 dark:text-gray-300">{listing.category}</span>
              </div>

              {listing.address && (
                <div className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
                  <i className="ri-map-pin-line text-gray-400 mt-0.5"></i>
                  <span>{listing.address}</span>
                </div>
              )}

              <div className="flex items-center gap-2 text-sm">
                <i className={listing.is_open === null ? 'ri-time-line text-gray-400' : 'ri-time-line text-green-600'}></i>
                <span className={listing.is_open === null ? 'font-medium text-gray-500' : 'font-medium text-green-600'}>
                  {listing.is_open === null ? 'Open status unavailable' : listing.is_open ? 'Open now' : 'Closed'}
                </span>
                {listing.hours_today && <span className="text-gray-500">{listing.hours_today}</span>}
              </div>

              {listing.phone && (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                  <i className="ri-phone-line text-gray-400"></i>
                  <span>{listing.phone}</span>
                </div>
              )}

              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([listing.business_name, listing.address].filter(Boolean).join(' '))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300"
              >
                <i className="ri-google-line"></i>
                View on Google
              </a>

              <div className="flex gap-2 pt-2">
                {[
                  {
                    icon: 'ri-direction-line',
                    label: 'Directions',
                    href: `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent([listing.business_name, listing.address].filter(Boolean).join(' '))}`,
                  },
                  {
                    icon: 'ri-phone-line',
                    label: 'Call',
                    href: listing.phone ? `tel:${listing.phone.replace(/[^\d+]/g, '')}` : '',
                  },
                  {
                    icon: 'ri-global-line',
                    label: 'Website',
                    href: listing.website || '',
                  },
                ].map((b) =>
                  b.href ? (
                    <a
                      key={b.label}
                      href={b.href}
                      target={b.href.startsWith('tel:') ? undefined : '_blank'}
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      <i className={b.icon}></i>
                      {b.label}
                    </a>
                  ) : (
                    <button
                      key={b.label}
                      disabled
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-400 text-sm font-medium cursor-not-allowed opacity-60"
                    >
                      <i className={b.icon}></i>
                      {b.label}
                    </button>
                  ),
                )}
                <button
                  onClick={handleShare}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-share-line"></i>
                  Share
                </button>
              </div>

              {listing.latest_post && (
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <i className="ri-article-line text-teal-600 text-xs"></i>
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Latest post</span>
                  </div>
                  <p className="text-xs text-gray-700 dark:text-gray-300">{listing.latest_post}</p>
                </div>
              )}

              {(listing.reviews || []).length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Reviews</h4>
                  {listing.reviews.map((review: any, i: number) => (
                    <div key={i} className="flex items-start gap-2">
                      <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-gray-600 dark:text-gray-300">
                        {(review.reviewer_name || 'A').charAt(0)}
                      </div>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-medium text-gray-800 dark:text-gray-200">{review.reviewer_name}</span>
                          <div className="flex">
                            {Array.from({ length: 5 }).map((_, s) => (
                              <i key={s} className={`ri-star-fill text-[10px] ${s < (review.rating || 0) ? 'text-yellow-400' : 'text-gray-200 dark:text-gray-600'}`}></i>
                            ))}
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{review.comment}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4">Listing Health Checklist</h3>
            {checklist.length > 0 ? (
              <div className="space-y-3">
                {checklist.map((item: any) => {
                  const done = item.status === 'done';
                  return (
                    <div key={item.item} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${done ? 'bg-green-100' : 'bg-red-100'}`}>
                          {done ? (
                            <i className="ri-check-line text-xs text-green-600"></i>
                          ) : (
                            <i className="ri-close-line text-xs text-red-600"></i>
                          )}
                        </div>
                        <span className={`text-sm ${done ? 'text-gray-700 dark:text-gray-200' : 'text-gray-500'}`}>{item.item}</span>
                      </div>
                      <span className={`text-xs font-medium ${done ? 'text-green-600' : 'text-red-600'}`}>
                        {done ? 'Done' : 'Missing'}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <GMBEmptyState title="No checklist data" message="Health checklist will appear once your profile syncs." />
            )}

            <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Overall Health</span>
                <span className="text-sm font-bold text-gray-900 dark:text-white">{data.overall_health_pct ?? 0}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div className="bg-teal-500 h-2 rounded-full" style={{ width: `${data.overall_health_pct ?? 0}%` }}></div>
              </div>
              <div className="text-[10px] text-gray-400 mt-1.5">Last synced {data.last_synced_label || '—'}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-teal-50 dark:bg-teal-900/20 rounded-lg flex items-center justify-center text-teal-600">
              <i className="ri-pulse-line text-sm" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200">Profile Monitoring</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Continuously monitors important GMB attributes and flags operational issues</p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-1.5">
            <div className="relative w-2 h-2">
              <div className="absolute inset-0 bg-amber-500 rounded-full animate-ping opacity-70" />
              <div className="relative w-2 h-2 bg-amber-500 rounded-full" />
            </div>
            <span className="text-xs text-amber-700 dark:text-amber-300 font-medium">
              {flaggedIssues.length > 0 ? 'Needs attention' : 'All good'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {monitoring.map((check: any) => {
            const style = checkStatusStyle[check.severity || 'good'] || checkStatusStyle.warning;
            return (
              <div key={check.check_type} className={`rounded-lg border p-4 shadow-sm ${style.bg}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-md bg-white/80 dark:bg-gray-800/60 flex items-center justify-center">
                      <i className={`${monitoringIcons[check.check_type] || 'ri-pulse-line'} text-sm ${style.icon.split(' ')[1]}`} />
                    </div>
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{check.check_type}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className={`w-2 h-2 rounded-full ${style.dot}`} />
                    <span className={`text-[10px] font-semibold uppercase tracking-wide ${style.text}`}>{check.severity}</span>
                  </div>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">{check.detail}</p>
                {check.trend && (
                  <div className={`mt-2 text-[11px] font-medium ${style.text} bg-white/60 dark:bg-gray-800/60 rounded-md px-2 py-1`}>
                    {check.trend}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {flaggedIssues.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">Flagged Issues</h4>
            <div className="space-y-3">
              {flaggedIssues.map((issue: any, i: number) => {
                const style = checkStatusStyle[issue.severity] || checkStatusStyle.warning;
                const targetSection = monitoringNavMap[issue.check_type];
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => targetSection && onNavigate?.(targetSection)}
                    className={`block w-full text-left bg-white dark:bg-gray-800 rounded-lg border ${style.bg.split(' ')[1]} shadow-sm p-4 transition-all hover:shadow-md ${targetSection ? 'cursor-pointer hover:-translate-y-0.5' : 'cursor-default'}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-md bg-white/60 dark:bg-gray-700 flex items-center justify-center flex-shrink-0`}>
                        <i className={`${style.icon} text-sm`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${style.text} ${style.bg.split(' ')[0]}`}>
                            {issue.severity}
                          </span>
                        </div>
                        <h5 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{issue.check_type}</h5>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">{issue.detail}</p>
                        {targetSection && (
                          <div className="flex items-center gap-1 mt-2 text-[11px] font-medium text-teal-600 dark:text-teal-400">
                            Resolve in Platform
                            <i className="ri-arrow-right-line text-xs" />
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}